import { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter } from 'react-router-dom';
import { Layout } from './components/Layout';
import { NotFound } from './components/NotFound';
import { HomePanel } from './panels/HomePanel';
import { SQLQueryPanel } from './panels/SQLQueryPanel';
import { GraphQLQueryPanel } from './panels/GraphQLQueryPanel';
import { StoreDataPanel } from './panels/StoreDataPanel';
import { AnalyticsPanel } from './panels/AnalyticsPanel';
import { UnifiedSubgrovesPanel } from './panels/UnifiedSubgrovesPanel';
import { RegisterSubgrovePanel } from './panels/RegisterSubgrovePanel';
import { NetworkPanel } from './panels/NetworkPanel';
import { PricingPanel } from './panels/PricingPanel';
import { VerifyPanel } from './panels/VerifyPanel';
import { ProofViewerPanel } from './panels/ProofViewerPanel';
import { ProofInspectorPanel } from './panels/ProofInspectorPanel';
import { SignupPanel } from './panels/SignupPanel';
import { LoginPanel } from './panels/LoginPanel';
import { AccountPanel } from './panels/AccountPanel';
import { KeysPanel } from './panels/KeysPanel';
import { UsagePanel } from './panels/UsagePanel';
import { BillingPanel } from './panels/BillingPanel';
import { AccountLayout } from './components/AccountLayout';
import { ForgotPasswordPanel } from './panels/ForgotPasswordPanel';
import { ResetPasswordPanel } from './panels/ResetPasswordPanel';
import { SetPasswordPanel } from './panels/SetPasswordPanel';
import { VerifyEmailPanel } from './panels/VerifyEmailPanel';
import { SubgrovePanel } from './panels/SubgrovePanel';
import { AuthRequired } from './components/AuthRequired';
import { useConnection } from './lib/connection';
import { SHOW_AUTH_PAGES, SHOW_SUBGROVES } from './lib/flags';
import { useOpenProofInVerify } from './lib/verify-prefill';

// Pull gkr-wasm / @a16z/helios / fixture JSON out of the initial bundle —
// only loaded when the user navigates to the matching /verify subroute.
const VerifiableRpcPanel = lazy(() =>
  import('./panels/VerifiableRpcPanel').then((m) => ({ default: m.VerifiableRpcPanel })),
);
const HeliosPanel = lazy(() =>
  import('./panels/HeliosPanel').then((m) => ({ default: m.HeliosPanel })),
);
const EventsHashPanel = lazy(() =>
  import('./panels/EventsHashPanel').then((m) => ({ default: m.EventsHashPanel })),
);

const SUSPENSE_FALLBACK = <div className="panel">Loading…</div>;

function lazyRoute(node: React.ReactNode) {
  return <Suspense fallback={SUSPENSE_FALLBACK}>{node}</Suspense>;
}

function HomeRoute() {
  return <HomePanel />;
}

function SQLRoute() {
  const conn = useConnection();
  const { openStructured } = useOpenProofInVerify();
  return (
    <SQLQueryPanel
      apiUrl={conn.indexerUrl || conn.apiUrl}
      listingApiUrl={conn.apiUrl}
      onOpenProof={openStructured}
    />
  );
}

function GraphQLRoute() {
  const conn = useConnection();
  const { openStructured } = useOpenProofInVerify();
  return (
    <GraphQLQueryPanel
      apiUrl={conn.indexerUrl || conn.apiUrl}
      listingApiUrl={conn.apiUrl}
      onOpenProof={openStructured}
    />
  );
}

function WriteRoute() {
  const conn = useConnection();
  return <StoreDataPanel apiUrl={conn.apiUrl} />;
}

function ChartsRoute() {
  const conn = useConnection();
  const { openStructured } = useOpenProofInVerify();
  return (
    <AnalyticsPanel
      apiUrl={conn.apiUrl}
      indexerUrl={conn.indexerUrl}
      onOpenProof={openStructured}
    />
  );
}

function SubgrovesRoute() {
  const conn = useConnection();
  return <UnifiedSubgrovesPanel apiUrl={conn.apiUrl} />;
}

function RegisterSubgroveRoute() {
  const conn = useConnection();
  return <RegisterSubgrovePanel apiUrl={conn.apiUrl} />;
}

function NetworkRoute() {
  const conn = useConnection();
  return <NetworkPanel apiUrl={conn.apiUrl} />;
}

const subgrovesRoutes = {
  path: 'subgroves',
  children: [
    {
      index: true,
      element: (
        <AuthRequired feature="Subgroves">
          <SubgrovesRoute />
        </AuthRequired>
      ),
    },
    {
      path: 'register',
      element: (
        <AuthRequired feature="Register Subgrove">
          <RegisterSubgroveRoute />
        </AuthRequired>
      ),
    },
    {
      path: ':id',
      element: (
        <AuthRequired feature="subgrove details">
          <SubgrovePanel />
        </AuthRequired>
      ),
      children: [
        { index: true, element: <ChartsRoute /> },
        { path: 'sql', element: <SQLRoute /> },
        { path: 'graphql', element: <GraphQLRoute /> },
        { path: 'write', element: <WriteRoute /> },
      ],
    },
  ],
};

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      // RPC customers land on their account (or sign-in); the network
      // snapshot that used to be the home page stays reachable at /home.
      {
        index: true,
        element: SHOW_AUTH_PAGES ? <Navigate to="/account" replace /> : <HomeRoute />,
      },
      { path: 'home', element: <HomeRoute /> },
      ...(SHOW_SUBGROVES
        ? [subgrovesRoutes]
        : []),
      { path: 'network', element: <NetworkRoute /> },
      { path: 'pricing', element: <PricingPanel /> },
      ...(SHOW_AUTH_PAGES
        ? [
            { path: 'signup', element: <SignupPanel /> },
            { path: 'login', element: <LoginPanel /> },
            { path: 'forgot-password', element: <ForgotPasswordPanel /> },
            { path: 'reset-password', element: <ResetPasswordPanel /> },
            { path: 'set-password', element: <SetPasswordPanel /> },
            { path: 'verify-email', element: <VerifyEmailPanel /> },
            // The RPC account pages. Not under /account/*: in production the
            // Pages Function proxies every /account/<sub> path to willow-saas,
            // so only the bare /account is a page route there.
            {
              element: <AccountLayout />,
              children: [
                { path: 'account', element: <AccountPanel /> },
                { path: 'keys', element: <KeysPanel /> },
                { path: 'usage', element: <UsagePanel /> },
                { path: 'billing', element: <BillingPanel /> },
              ],
            },
          ]
        : []),
      {
        path: 'verify',
        element: <VerifyPanel />,
        children: [
          {
            index: true,
            element: <Navigate to={SHOW_SUBGROVES ? 'viewer' : 'inspector'} replace />,
          },
          // Both mean nothing without subgroves; an old link lands on a tab that works.
          {
            path: 'vrpc',
            element: SHOW_SUBGROVES ? (
              lazyRoute(<VerifiableRpcPanel />)
            ) : (
              <Navigate to="/verify/inspector" replace />
            ),
          },
          {
            path: 'viewer',
            element: SHOW_SUBGROVES ? (
              <ProofViewerPanel />
            ) : (
              <Navigate to="/verify/inspector" replace />
            ),
          },
          { path: 'inspector', element: <ProofInspectorPanel /> },
          { path: 'helios', element: lazyRoute(<HeliosPanel />) },
          { path: 'events-hash', element: lazyRoute(<EventsHashPanel />) },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
